import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { servicesAPI, vehicleAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, MapPin, Car, Navigation } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const ServiceRequest = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const service = location.state?.service;
  const [vehicles, setVehicles] = useState([]);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [loadingDestination, setLoadingDestination] = useState(false);
  const [formData, setFormData] = useState({
    location: '',
    latitude: null,
    longitude: null,
    destination: '',
    destinationLatitude: null,
    destinationLongitude: null,
    selectedVehicleId: '',
    vehicleCondition: '',
    tireSize: '',
    isAlloyWheel: false,
    fuelType: '',
    issue: ''
  });

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const data = await vehicleAPI.getVehicles();
      setVehicles(data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: 'Error',
        description: 'Geolocation is not supported by your browser',
        variant: 'destructive'
      });
      return;
    }

    setLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await response.json();
          
          setFormData({
            ...formData,
            latitude,
            longitude,
            location: data.display_name || `${latitude}, ${longitude}`
          });
          
          toast({
            title: 'Location Found!',
            description: 'Your current location has been set'
          });
        } catch (error) {
          setFormData({
            ...formData,
            latitude,
            longitude,
            location: `${latitude}, ${longitude}`
          });
        }
        setLoadingLocation(false);
      },
      (error) => {
        toast({
          title: 'Location Error',
          description: 'Could not get your location. Please enter manually.',
          variant: 'destructive'
        });
        setLoadingLocation(false);
      }
    );
  };

  const handleGetDestination = () => {
    if (!navigator.geolocation) {
      toast({
        title: 'Error',
        description: 'Geolocation is not supported by your browser',
        variant: 'destructive'
      });
      return;
    }

    setLoadingDestination(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await response.json();
          
          setFormData({
            ...formData,
            destinationLatitude: latitude,
            destinationLongitude: longitude,
            destination: data.display_name || `${latitude}, ${longitude}`
          });
          
          toast({
            title: 'Destination Set!',
            description: 'Drop-off location has been set'
          });
        } catch (error) {
          setFormData({
            ...formData,
            destinationLatitude: latitude,
            destinationLongitude: longitude,
            destination: `${latitude}, ${longitude}`
          });
        }
        setLoadingDestination(false);
      },
      (error) => {
        toast({
          title: 'Location Error',
          description: 'Could not get location. Please enter manually.',
          variant: 'destructive'
        });
        setLoadingDestination(false);
      }
    );
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.selectedVehicleId && vehicles.length > 0) {
      toast({
        title: 'Please Select a Vehicle',
        description: 'Choose a vehicle from your garage',
        variant: 'destructive'
      });
      return;
    }

    // For vehicle recovery, destination and condition are required
    if (service.id === 'tow') {
      if (!formData.destination) {
        toast({
          title: 'Destination Required',
          description: 'Please specify where to drop off the vehicle',
          variant: 'destructive'
        });
        return;
      }
      if (!formData.vehicleCondition) {
        toast({
          title: 'Vehicle Condition Required',
          description: 'Please specify if vehicle starts, rolls, or is keyless',
          variant: 'destructive'
        });
        return;
      }
    }

    // For tire change, tire size is required
    if (service.id === 'tire' && !formData.tireSize) {
      toast({
        title: 'Tire Size Required',
        description: 'Please specify the tire size (e.g., 205/55R16)',
        variant: 'destructive'
      });
      return;
    }

    // For fuel delivery, fuel type is required
    if (service.id === 'fuel' && !formData.fuelType) {
      toast({
        title: 'Fuel Type Required',
        description: 'Please select the fuel type',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      const requestData = {
        service_type: service.id,
        location: formData.location,
        latitude: formData.latitude,
        longitude: formData.longitude,
        destination: formData.destination,
        destination_latitude: formData.destinationLatitude,
        destination_longitude: formData.destinationLongitude,
        vehicle_id: formData.selectedVehicleId || null,
        vehicle_condition: formData.vehicleCondition,
        tire_size: formData.tireSize,
        is_alloy_wheel: formData.isAlloyWheel,
        fuel_type: formData.fuelType,
        issue_description: formData.issue
      };
      
      const response = await servicesAPI.createServiceRequest(requestData);
      
      toast({
        title: 'Request Submitted!',
        description: 'Drivers will start sending you quotes'
      });
      
      navigate('/customer/quotes', {
        state: {
          service,
          serviceRequestId: response.service_request.id
        }
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create service request',
        variant: 'destructive'
      });
    }
  };

  if (!service) {
    navigate('/customer/dashboard');
    return null;
  }

  const selectedVehicle = vehicles.find(v => v.id === formData.selectedVehicleId);

  return (
    <div className="min-h-screen bg-wheely-navy-950">
      <div className="bg-gradient-to-r from-wheely-navy-950 to-wheely-navy-900 border-b border-wheely-orange/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/customer/dashboard')}
            className="text-wheely-orange hover:text-wheely-orange-light hover:bg-wheely-orange/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-3xl font-bold text-white mb-6">Request {service.name}</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <Card className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-wheely-orange" />
                    Location
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="location" className="text-white">Your Location</Label>
                    <div className="flex gap-2">
                      <Input
                        id="location"
                        name="location"
                        placeholder="123 Main St, City"
                        value={formData.location}
                        onChange={handleChange}
                        required
                        className="bg-wheely-navy-900 border-wheely-navy-700 text-white placeholder:text-gray-500 flex-1"
                      />
                      <Button
                        type="button"
                        onClick={handleGetLocation}
                        disabled={loadingLocation}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {loadingLocation ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Navigation className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400">Click the GPS button to use your current location</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Car className="w-5 h-5 text-wheely-orange" />
                    Vehicle
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {vehicles.length > 0 ? (
                    <div className="space-y-2">
                      <Label htmlFor="vehicle" className="text-white">Select Vehicle from Garage</Label>
                      <Select value={formData.selectedVehicleId} onValueChange={(value) => setFormData({...formData, selectedVehicleId: value})}>   
                        <SelectTrigger className="bg-wheely-navy-900 border-wheely-navy-700 text-white">
                          <SelectValue placeholder="Choose a vehicle" />
                        </SelectTrigger>
                        <SelectContent className="bg-wheely-navy-900 border-wheely-navy-700">
                          {vehicles.map((vehicle) => (
                            <SelectItem key={vehicle.id} value={vehicle.id} className="text-white hover:bg-wheely-navy-800">
                              {vehicle.nickname || `${vehicle.make} ${vehicle.model}`} - {vehicle.registration}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedVehicle && (
                        <div className="bg-wheely-navy-900 rounded-lg p-3 mt-2">
                          <p className="text-sm text-gray-400">Selected:</p>
                          <p className="text-white font-semibold">{selectedVehicle.make} {selectedVehicle.model}{selectedVehicle.year ? ` (${selectedVehicle.year})` : ''}</p>
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate('/customer/garage')}
                        className="w-full mt-2 border-wheely-orange/50 text-wheely-orange hover:bg-wheely-orange/10"
                      >
                        Add New Vehicle to Garage
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-400 mb-3">No vehicles in your garage</p>
                      <Button
                        type="button"
                        onClick={() => navigate('/customer/garage')}
                        className="bg-gradient-to-r from-wheely-orange to-wheely-orange-dark hover:from-wheely-orange-dark hover:to-orange-700"
                      >
                        Add Vehicle to Garage
                      </Button>
                    </div>
                  )}

                  {/* Vehicle Condition for Towing */}
                  {service.id === 'tow' && (
                    <div className="space-y-2">
                      <Label htmlFor="vehicleCondition" className="text-white">Vehicle Condition</Label>
                      <Select value={formData.vehicleCondition} onValueChange={(value) => setFormData({...formData, vehicleCondition: value})}>
                        <SelectTrigger className="bg-wheely-navy-900 border-wheely-navy-700 text-white">
                          <SelectValue placeholder="Select vehicle condition" />
                        </SelectTrigger>
                        <SelectContent className="bg-wheely-navy-900 border-wheely-navy-700">
                          <SelectItem value="starts_and_runs" className="text-white hover:bg-wheely-navy-800">
                            Starts and Runs - Vehicle can be driven onto trailer
                          </SelectItem>
                          <SelectItem value="rolls_neutral" className="text-white hover:bg-wheely-navy-800">
                            Rolls in Neutral - Vehicle can be pushed/pulled
                          </SelectItem>
                          <SelectItem value="keyless_stuck" className="text-white hover:bg-wheely-navy-800">
                            Keyless/Stuck - Vehicle cannot move (winch required)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400">This affects pricing - keyless/stuck vehicles require special equipment</p>
                    </div>
                  )}
                  
                  {/* Tire Size and Alloy Wheel for Tire Change */}
                  {service.id === 'tire' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="tireSize" className="text-white">Tire Size *</Label>
                        <Input
                          id="tireSize"
                          name="tireSize"
                          placeholder="e.g., 205/55R16 or 225/45R17"
                          value={formData.tireSize}
                          onChange={handleChange}
                          required
                          className="bg-wheely-navy-900 border-wheely-navy-700 text-white placeholder:text-gray-500"
                        />
                        <p className="text-xs text-gray-400">Check your tire sidewall for the size</p>
                      </div>
                      <div className="flex items-center space-x-2 bg-wheely-navy-900 p-4 rounded-lg">
                        <Checkbox
                          id="isAlloyWheel"
                          checked={formData.isAlloyWheel}
                          onCheckedChange={(checked) => setFormData({...formData, isAlloyWheel: checked})}
                          className="border-wheely-orange data-[state=checked]:bg-wheely-orange"
                        />
                        <Label
                          htmlFor="isAlloyWheel"
                          className="text-white font-normal cursor-pointer"
                        >
                          Alloy Wheel (affects handling requirements)
                        </Label>
                      </div>
                    </>
                  )}

                  {/* Fuel Type for Fuel Delivery */}
                  {service.id === 'fuel' && (
                    <div className="space-y-2">
                      <Label htmlFor="fuelType" className="text-white">Fuel Type *</Label>
                      <Select value={formData.fuelType} onValueChange={(value) => setFormData({...formData, fuelType: value})}>
                        <SelectTrigger className="bg-wheely-navy-900 border-wheely-navy-700 text-white">
                          <SelectValue placeholder="Select fuel type" />
                        </SelectTrigger>
                        <SelectContent className="bg-wheely-navy-900 border-wheely-navy-700">
                          <SelectItem value="petrol" className="text-white hover:bg-wheely-navy-800">
                            Petrol/Gasoline
                          </SelectItem>
                          <SelectItem value="diesel" className="text-white hover:bg-wheely-navy-800">
                            Diesel
                          </SelectItem>
                          <SelectItem value="electric" className="text-white hover:bg-wheely-navy-800">
                            Electric (Charge Required)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400">Ensure you select the correct fuel type for your vehicle</p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="issue" className="text-white">Describe the Issue</Label>
                    <Textarea
                      id="issue"
                      name="issue"
                      placeholder="Tell us what happened..."
                      value={formData.issue}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="bg-wheely-navy-900 border-wheely-navy-700 text-white placeholder:text-gray-500 resize-none"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Destination for Vehicle Recovery */}
              {service.id === 'tow' && (
                <Card className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-green-500" />
                      Drop-off Destination
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="destination" className="text-white">Where to drop off the vehicle?</Label>
                      <div className="flex gap-2">
                        <Input
                          id="destination"
                          name="destination"
                          placeholder="Garage address or destination"
                          value={formData.destination}
                          onChange={handleChange}
                          required
                          className="bg-wheely-navy-900 border-wheely-navy-700 text-white placeholder:text-gray-500 flex-1"
                        />
                        <Button
                          type="button"
                          onClick={handleGetDestination}
                          disabled={loadingDestination}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {loadingDestination ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Navigation className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-400">Specify the destination so drivers can quote accurately</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Button type="submit" className="w-full bg-gradient-to-r from-wheely-orange to-wheely-orange-dark hover:from-wheely-orange-dark hover:to-orange-700 text-white font-semibold text-lg py-6">
                Submit Request - Get Quotes
              </Button>
            </form>
          </div>

          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-wheely-orange to-wheely-orange-dark border-0">
              <CardHeader>
                <CardTitle className="text-white text-2xl">How It Works</CardTitle>
                <CardDescription className="text-orange-100">Competitive quote system</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-white">
                <div className="flex items-start gap-3">
                  <div className="bg-white text-wheely-orange-dark rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                  <p className="text-orange-100">Submit your request with location and vehicle details</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-white text-wheely-orange-dark rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                  <p className="text-orange-100">Multiple drivers will send you competitive quotes</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-white text-wheely-orange-dark rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                  <p className="text-orange-100">View quotes sorted from lowest to highest price</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-white text-wheely-orange-dark rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">4</div>
                  <p className="text-orange-100">Choose the best quote and complete the service</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20">
              <CardHeader>
                <CardTitle className="text-white">Benefits</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-gray-400">
                <p>✓ Get the best price through competition</p>
                <p>✓ See driver ratings and reviews</p>
                <p>✓ Choose your preferred driver</p>
                <p>✓ No hidden fees or surprises</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceRequest;